import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Table } from "antd";
import axios from "axios";
import DeleteModal from "../../../components/modelpopup/DeleteModal";
import SearchBox from "../../../components/SearchBox";
import DepartmentModal from "../../../components/modelpopup/DepartmentModal";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from 'moment';

const Department = () => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const dateFormat = localStorage.getItem("dateFormat") || "MMM DD, YYYY";
  const dateFormatWithTime = localStorage.getItem("dateFormatWithTime") || "MMM DD, YYYY hh:mm A";

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const [isFullScreen, setFullScreen] = useState(false);
  const maximizeBtnRef = useRef(null);

  useEffect(() => {
    const handleClick = () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen();
        setFullScreen(true);
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
          setFullScreen(false);
        }
      }
    };

    const cleanup = () => {
      if (isFullScreen && document.exitFullscreen) {
        document.exitFullscreen();
        setFullScreen(false);
      }
    };

    const maximizeBtn = maximizeBtnRef.current;
    maximizeBtn.addEventListener('click', handleClick);

    return () => {
      maximizeBtn.removeEventListener('click', handleClick);
      cleanup();
    };
  }, [isFullScreen]);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${apiUrl}/departments/listdepartment`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setDepartments(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching departments:", error);
        setLoading(false);
      }
    };

    fetchDepartments();
  }, [token]);

  const onSubmit = (responseData) => {
    const updatedDepartments = editingDepartment
      ? departments.map((dept) =>
          dept.id === responseData.data.id ? responseData.data : dept
        )
      : [...departments, responseData.data];

    setDepartments(updatedDepartments);
    setEditingDepartment(null);
  };

  const deleteDepartment = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this department!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${apiUrl}/departments/delete/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setDepartments(departments.filter((dept) => dept.id !== id));
        toast.success("Department has been deleted.");
      } catch (error) {
        let errorMessage = "Failed to delete department. Please try again later.";
        if (error.response && error.response.data && error.response.data.error) {
          errorMessage = error.response.data.error;
        }
        Swal.fire("Error!", errorMessage, "error");
      }
    }
  };

  const handleEditClick = (department) => {
    setEditingDepartment(department);
  };

  const handleAddClick = () => {
    setEditingDepartment(null);
  };

  const columns = [
    {
      title: "Department Name",
      dataIndex: "department",
      sorter: (a, b) => a.department.localeCompare(b.department),
      width: "20%",
    },
    {
      title: "#Employees",
      dataIndex: "employee_count",
      sorter: (a, b) => a.employee_count - b.employee_count,
      width: "10%",
    },
    {
      title: "Created On",
      dataIndex: "created",
      render: (date) => date ? moment(date).format(dateFormatWithTime) : '-',
      sorter: (a, b) => moment(a.created).diff(moment(b.created)),
      width: "20%",
    },
    {
      title: "Created By",
      dataIndex: "createdbyname",
      sorter: (a, b) => a.createdbyname.localeCompare(b.createdbyname),
      width: "10%",
    },
    {
      title: "Modified On",
      dataIndex: "modified",
      render: (date) => date ? moment(date).format(dateFormatWithTime) : '-',
      sorter: (a, b) => moment(a.modified).diff(moment(b.modified)),
      width: "20%",
    },
    {
      title: "Modified By",
      dataIndex: "modifiedbyname",
      sorter: (a, b) => a.modifiedbyname.localeCompare(b.modifiedbyname),
      width: "10%",
    },
    {
      title: "Action",
      render: (_, record) => (
        <div className="dropdown dropdown-action">
          <Link
            to="#"
            className="action-icon dropdown-toggle"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i className="material-icons">more_vert</i>
          </Link>
          <div className="dropdown-menu dropdown-menu-right">
            <Link
              className="dropdown-item"
              to="#"
              onClick={() => handleEditClick(record)}
              data-bs-toggle="modal"
              data-bs-target="#add_department"
            >
              <i className="fa fa-pencil m-r-5" /> Edit
            </Link>
            <button className="dropdown-item" onClick={() => deleteDepartment(record.id)}>
              <i className="fa fa-trash m-r-5" /> Delete
            </button>
          </div>
        </div>
      ),
      width: "20%",
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="page-header page-top-icons">
          <div className="row align-items-center">
            <div className="col-md-4">
              <h3 className="page-title">Departments</h3>
            </div>
            <div className="col-md-8 float-end ms-auto">
              <div className="d-flex title-head">
                <div className="view-icons">
                  <Link to="#" className="grid-view btn btn-link">
                    <i className="las la-redo-alt" />
                  </Link>
                  <Link to="#" className="list-view btn btn-link" id="collapse-header" ref={maximizeBtnRef}>
                    <i className="las la-expand-arrows-alt" />
                  </Link>
                </div>
                <Link to="#" className="btn add-btn custom-btn" onClick={() => handleAddClick()} data-bs-toggle="modal" data-bs-target="#add_department">
                <i className="la la-plus-circle custom-icon" /> Add New
                </Link>
              </div>
            </div>
          </div>
        </div>
        
        <div className="row">
          <div className="col-md-12">
            <div className="table-responsive">
              <SearchBox />
              <Table
                columns={columns}
                dataSource={departments}
                loading={loading}
                rowKey={(record) => record.id}
              />
            </div>
          </div>
        </div>
      </div>
      <DepartmentModal onSubmit={onSubmit} departmentData={editingDepartment} />
    </div>
  );
};

export default Department;
